import { authClient } from "@/lib/auth-client";
import { MemberService } from "@/services/MemberService";
import { log } from "./logger";
import { tprStorage } from "./storage";
import { setKnownUser } from "./knownUser";
import type { Member, MemberLoginResponse } from "../types/Member";
import type { Booking } from "@/types/Booking";

export type LoginResponse =
  | ({ success: true } & Pick<
      MemberLoginResponse,
      "termsAccepted" | "currentTermsVersion" | "annualDeclarationAccepted"
    >)
  | Error;

export const login = async (email: string, password: string): Promise<LoginResponse> => {
  if (!email) return Error("Can't login. No email provided");
  if (!password) return Error("Can't login. No password provided");

  try {
    const { data, error } = await authClient.signIn.email({
      email: email,
      password: password,
    });

    if (error) {
      log("auth", `login: Error logging in member with email ${email}: ${error.message}`);
      return new Error("Invalid email or password");
    }

    log("auth", `login: Successfully logged in member with email ${email}`, data);

    // Store bearer token for Safari ITP compatibility
    if (data?.token) {
      sessionStorage.setItem("tpr-bearer-token", data.token);
      log("auth", `login: Stored bearer token for cross-browser compatibility`);
    }

    // here, now we have a valid token, we can access ourselves securely
    const memberService = new MemberService();
    const member = await memberService.loginMember();
    if (member instanceof Error) {
      log("auth", `login: Error fetching logged in member data for email ${email}: ${member.message}`);
      return new Error("Error fetching member data after login");
    }

    // check to see if we have at least one permissable location
    // if (!member.member.permissableLocationIds || member.member.permissableLocationIds.length === 0) {
    //   log("auth", `login: No permissible locations found for member ID ${member.member.id} after login`);
    //   return new Error("No permissible locations found for member");
    // }

    // check our role, we can only login if we are not prospect
    if (member.member.role.includes("prospect")) {
      log("auth", `login: Member ID ${member.member.id} has prospect role, cannot login`);
      return new Error("Prospect members cannot login");
    }

    if (member.member.active === false) {
      log("auth", `login: Member ID ${member.member.id} is inactive, cannot login`);
      return new Error("Your account is inactive. Please contact support.");
    }

    // Store the member after successful login. Carry the login-gate acceptance (computed
    // server-side) onto the stored member so the TPR route guard can enforce terms/declaration on
    // every protected route — not just at the login redirect (which the back button could bypass).
    tprStorage.set("tpruser", {
      ...member.member,
      termsAccepted: member.termsAccepted,
      annualDeclarationAccepted: member.annualDeclarationAccepted,
      currentTermsVersion: member.currentTermsVersion,
    });

    setKnownUser({
      email: member.member.email,
      firstName: member.member.firstName,
      avatar: member.member.avatar,
    });

    return {
      success: true,
      termsAccepted: member.termsAccepted,
      currentTermsVersion: member.currentTermsVersion,
      annualDeclarationAccepted: member.annualDeclarationAccepted,
    };
  } catch (error) {
    // Handle network errors, connection failures, timeouts, etc.
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    log("auth", `login: Network or unexpected error during login for email ${email}: ${errorMessage}`);
    return new Error("Unable to connect to the server. Please check your connection and try again.");
  }
};

export const getToken = (): string | null => {
  const storedMember = tprStorage.get("tpruser");
  if (!storedMember) return null;
  return storedMember.token || null;
};

export const amILookingAtMyself = (memberId: string | null) => {
  const storedMember = tprStorage.get("tpruser");
  if (!storedMember || !memberId) return false;
  return storedMember.id === memberId;
};

// will return true if member has system or superadmin role
export const isSystemAccount = (member: Member | null) => {
  return member?.role?.includes("system") || member?.role?.includes("superadmin") || false;
};

export const isStaff = (member: Member | null) => {
  return (
    member?.role?.includes("system") ||
    member?.role?.includes("practice_manager") ||
    member?.role?.includes("admin") ||
    member?.role?.includes("superadmin") ||
    false
  );
};

export const isMemberAPracticeManager = (member: Member | null) => {
  return member?.role?.includes("practice_manager") || false;
};

export const isMemberAPracticeManagerForThisLocation = (member: Member, locationId: string) => {
  if (!isMemberAPracticeManager(member)) return false;
  return member.practiceManagerLocationIds?.includes(locationId) || false;
};

export const isMemberAnAdmin = (member: Member | null) => {
  return member?.role?.includes("admin") || member?.role?.includes("superadmin") || false;
};

export const isMemberASuperAdmin = (member: Member | null) => {
  return member?.role?.includes("superadmin") || false;
};

export const isMemberAnOrganisationOwner = (member: Member | null) => {
  if (!member) return false;
  // Check if organisation object is populated and member is the owner
  if (member.organisation?.ownerId === member.id) return true;
  // Also check ownerOfOrganisationId field (set when member owns an org but org object isn't populated)
  if (member.ownerOfOrganisationId) return true;
  return false;
};

/**
 * True when `member` is an organisation owner and `booking` belongs to a member of the org they own.
 * Used to highlight an owner's org bookings on the calendar (where bookings from arbitrary members
 * are shown). Relies on the enriched booking.member.organisationId.
 */
export const isOwnerOrgBooking = (member: Member | null, booking: Pick<Booking, "member">): boolean => {
  if (!isMemberAnOrganisationOwner(member)) return false;
  const ownedOrgId = member?.ownerOfOrganisationId ?? member?.organisation?.id;
  if (!ownedOrgId) return false;
  return booking.member?.organisationId === ownedOrgId;
};
